import { Component, OnDestroy, OnInit } from '@angular/core';
import { OnboardingV2Service } from '@mpt-fo/common/core/services/onboarding-v2.service';
import {
    BvnNinMisMatchError,
    ErrorCode,
    KycDictionary,
    KYCFormKeys,
    KYCLevel,
    KycLvl1FormStep,
    KycRoutes,
    TimelineStatus,
} from '@app/channels/onboarding/onboarding-v2/model/onboarding-v2-model';
import { TimelineModel } from '@mpt-fo/common/shared/models/timeline.model';
import { CustomTimelineVersion } from '@mpt-fo/common/kamona/model/kamonal-model';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { NibssVerificationService } from '@mpt-fo/common/core/services/nibss-verification.service';
import {
    NibssBvnFlow,
    NibssBvnVerificationData,
    NibssBvnVerificationResponse,
} from '@app/channels/onboarding/nibss-bvn-verification/models/nibss-bvn-verfication.model';
import { StorageKeys } from '@mpt-fo/common/shared/models/storage-keys';
import { DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';
import { initializeDatalayer } from '@mpt-fo/common/shared/models/data.layer.model';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { MergeDuplicateProfileAccountErrorCode } from '@app/channels/settings/merge-duplicate-nin-accounts/models/merge-account';
import { KycBvnNinMismatchModalComponent } from '../../../../../kyc-bvn-nin-mismatch-modal/kyc-bvn-nin-mismatch-modal.component';
import { Subscription } from 'rxjs';
import { EXISTING_PROFILE_CODE } from '@mpt-fo/common/core/models/onboarding';

@Component({
    selector: 'ng-mf-bo-kyc-lvl1-bvn-verification',
    templateUrl: './bo-kyc-lvl1-bvn-verification.component.html',
    styleUrls: ['./bo-kyc-lvl1-bvn-verification.component.scss'],
})
export class BoKycLvl1BvnVerificationComponent implements OnInit, OnDestroy {
    loading: boolean;
    submitting: boolean;
    formData = { bvn: '', dateOfBirth: '' };
    kycLvl1Steps: any;
    currentFormStep: TimelineModel;
    totalStep: number;
    previousLabel = KycDictionary.Level_1;
    Dictionary = KycDictionary;
    formSteps: TimelineModel[] = [];
    CustomTimelineVersion = CustomTimelineVersion;
    allowNibssBvnVerification: boolean;
    runningModal: NzModalRef;
    private resumeBvnNinUpgradeFlowSubscription: Subscription;

    constructor(
        public onboardingService: OnboardingV2Service,
        public modalService: ModalService,
        private nibssService: NibssVerificationService,
        public dataLayerHelper: DataLayerService,
        public localStorage: LocalStorageService,
    ) {}
    ngOnDestroy(): void {
        if (this.resumeBvnNinUpgradeFlowSubscription) {
            this.resumeBvnNinUpgradeFlowSubscription.unsubscribe();
        }
    }

    initialize() {
        this.allowNibssBvnVerification = this.onboardingService.useNibssBvnVerification();
        if (this.onboardingService.getBankVerificationNumberFromKyc()) {
            this.setBvnIfAlreadySubmitted(this.formData);
        }
        this.setFormParameters();
        const payload = this.getDataLayer('bo_onboarding_initiated');
        this.dataLayerHelper.logEvent(payload);
    }

    getDataLayer(event: string) {
        const datalayer = initializeDatalayer(event);
        datalayer.user_id = this.onboardingService.getSaveTokenData()?.userId;
        datalayer['bvn_onboarded'] = this.onboardingService.getBankVerificationNumberFromKyc();
        return datalayer;
    }

    setFormParameters() {
        const formParameters = this.onboardingService.getBoKycLvl1FormParameters(KYCFormKeys.BVN_COMPLETED);
        this.totalStep = formParameters?.totalStep;
        this.kycLvl1Steps = formParameters?.kycLvl1Steps;
        this.currentFormStep = formParameters?.currentFormStep;
        this.formSteps = formParameters?.formSteps;
    }

    setBvnIfAlreadySubmitted(formData: { bvn: string; dateOfBirth: string }) {
        formData.bvn = this.onboardingService.getBankVerificationNumberFromKyc();
    }

    ngOnInit(): void {}

    submit(bvnObj: any, existingCustomer = false) {
        this.submitting = true;
        const isBvnChanged = this.isBvnChanged(bvnObj?.value?.bvn, this.onboardingService.getBankVerificationNumberFromKyc());
        const isFailedBvn = this.onboardingService.bvnVerificationFailed();
        const canValidateBvn = !this.onboardingService.hasSubmittedBvn() || isBvnChanged || isFailedBvn;
        canValidateBvn ? this.validateBankVerificationNumber(bvnObj, existingCustomer) : this.NonNibssBvnVerification(bvnObj?.value?.bvn, false);
    }

    validateBankVerificationNumber(bvnObj, existingCustomer) {
        this.handleValidateBvn(bvnObj?.value, existingCustomer)
            .then((res) => {
                this.handleResponse(bvnObj?.value?.bvn, res);
            })
            .catch((e) => {
                this.handleValidateBvnError(e, bvnObj?.value?.bvn);
            })
            .finally(() => {
                this.loading = false;
            });
    }

    handleValidateBvnError(e, bvn) {
        if (e?.error?.responseCode === MergeDuplicateProfileAccountErrorCode.BVN && this.onboardingService.canUseDuplicateProfileMergeFlow()) {
            this.onboardingService.routeToDuplicateMergeFlow(e?.error, 'uprageKYCLevel2');
        } else if (e?.error?.code === ErrorCode.VALIDATE_BVN_NIN_MISMATCH) {
            this.launchBvnNinMismatchModal({ apiData: e.error, bvn });
        } else {
            this.handleError(e);
        }
    }

    launchBvnNinMismatchModal(data: { apiData: BvnNinMisMatchError; bvn?: string; nin?: string }) {
        this.submitting = false;
        const config = new ModalOptionsConfig();
        config.nzComponentParams.data = data;
        config.nzMaskClosable = true;
        this.runningModal = this.modalService.createModal(KycBvnNinMismatchModalComponent, config);
    }

    handleValidateBvn(bvnObj, existingCustomer) {
        const shouldUseUpgradedLvl2Flow = this.onboardingService.shouldUseUpgradeKycLevel2Flow();
        if (shouldUseUpgradedLvl2Flow) {
            return this.onboardingService.validKycBvnV3(bvnObj);
        }
        if (!this.onboardingService.useSimplifiedKycLevel2Flow()) {
            return this.onboardingService.validKycLvl1Bvn(bvnObj, existingCustomer);
        }
        return this.onboardingService.validKycLvl1BvnV2(bvnObj, existingCustomer);
    }

    isBvnChanged(bvn: string, savedBvn: string) {
        return bvn !== savedBvn;
    }

    goToNextOfKinStepForBvn(bvn: string) {
        this.saveData(bvn);
        this.updateKycDataWithBvnVerificationStatus();
        this.routeToNextStep();
    }

    NonNibssBvnVerification(bvn: string, canValidateBvn = true) {
        const shouldUseNewKycLvl2Flow = canValidateBvn && this.onboardingService.shouldUseUpgradeKycLevel2Flow();
        if (!shouldUseNewKycLvl2Flow) {
            this.goToNextOfKinStepForBvn(bvn);
        } else {
            this.onboardingService.routeToVerifyIdentity(KycRoutes.BO_LVL_2_ROUTE, KycRoutes.NIN_BVN_VERIFY_DETAILS, bvn);
            this.resumeBvnNinUpgradeFlowSubscription = this.onboardingService.getResumeNinBvnUpgradeFlow().subscribe((identityNumber) => {
                if (identityNumber) {
                    this.goToNextOfKinStepForBvn(identityNumber);
                    this.onboardingService.setResumeNinBvnUpgradeFlow('');
                }
            });
        }
    }

    updateKycDataWithBvnVerificationStatus() {
        this.onboardingService.updateKycDataWithBvnVerificationStatus(TimelineStatus.IN_REVIEW);
    }

    handleResponse(bvn: string, payload: NibssBvnVerificationResponse) {
        this.submitting = false;
        const shouldRedirect = this.allowNibssBvnVerification && payload?.shouldRedirect;
        shouldRedirect ? this.handleNibssBvnVerification(payload) : this.NonNibssBvnVerification(bvn);
    }

    handleNibssBvnVerification(payload) {
        this.setNibssBvnVerificationParameters();
        this.launchModal(payload);
    }

    setNibssBvnVerificationParameters() {
        const nibssBvnVerificationData: NibssBvnVerificationData = this.buildNibssBvnVerificationParameters();
        this.nibssService.setNibssVerificationData(nibssBvnVerificationData);
    }

    private buildNibssBvnVerificationParameters() {
        const nextSteps = this.kycLvl1Steps[KycLvl1FormStep.FACE_VERIFICATION];
        return {
            currentStep: this.currentFormStep?.step,
            nextSteps,
            redirectRoute: KycRoutes.BO_LVL_1_ROUTE,
            shouldUpdateStep: true,
            storageKey: StorageKeys.current_kyc_lvl1_step,
        };
    }

    routeToNextStep() {
        const kycStep = this.onboardingService.getKycLvl1Step();
        this.onboardingService.navigateKycLvl1Form(kycStep.FACE_VERIFICATION, false);
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    launchModal(payload: any) {}

    createNibssModalConfig(params, payload: NibssBvnVerificationResponse) {
        const config = params?.isMobile ? this.onboardingService.createMobileNibssModalConfig() : this.onboardingService.createWebNibssModalConfig();
        config.nzComponentParams.callback = (result) => { 
            this.handleCloseModal(result, payload);
        };
        params.isSubmit = true;
        config.nzComponentParams.data = params;
        return config;
    }

    handleCloseModal(result, payload: NibssBvnVerificationResponse) {
        if (result) {
            this.nibssService.setNibssFlow(NibssBvnFlow.BO_KYC_LVL_1);
            this.nibssService.handleNibssRouting(payload);
        }
    }

    saveData(bvn: string) {
        this.onboardingService.saveKycLevel1BvnData(bvn);
    }

    handleError(error) {
        const description = StringHelper.getErrorMessageHttpErrorResponse(error);
        const errorCode = error?.error?.responseCode;
        this.handleFailedBvnVerificationModal(errorCode, description);
    }

    handleFailedBvnVerificationModal(errorCode, description) {
        const isExitingMoniepointProfile = this.isExitingMoniepointProfile(errorCode, description);
        isExitingMoniepointProfile ? this.logoutModal(description) : this.launchErrorModal(description);
    }

    launchErrorModal(description) {
        this.onboardingService.launchErrorModal({ description });
        this.submitting = false;
    }

    logoutModal(description) {
        const title = 'Existing Moniepoint profile';
        const callbackName = 'Login';
        this.onboardingService.launchErrorModalV2({ description, callbackName, title }).then(() => {
            this.onboardingService.login();
            this.submitting = false;
        });
    }

    isExitingMoniepointProfile(errorCode, description: string) {
        const existingProfileDescription = 'login with the credentials';
        const errorMessage = description.toLowerCase();
        const isExistingProfileCode = errorCode === EXISTING_PROFILE_CODE;
        const isExistingProfileDescription = errorMessage.includes(existingProfileDescription);
        return isExistingProfileCode || isExistingProfileDescription;
    }

    goBack() {
        this.onboardingService.routeToKycPrimer(KYCLevel.LVL_1);
        
    }
}
